import { prettyFeelColorPalettes } from '@/shared/ui/theming';
import type { TooltipFormatterContext } from '@/ui/chart/builder/parts/tooltip.utils';
import type { ColorPaletteConfiguration } from '@/ui/color-picker/ui-color-picker.component';
import { buildDeterministicColorMap, hashStringToColor } from '@/ui/colors/colors.utils';
import { combineLatestDeepObject } from '@/utils/rxjs/generators/combine-latest-deep-object.generator';
import { extractValue } from '@/utils/rxjs/helpers/rxjs.helpers';
import { concatReducer } from '@/utils/toolkit/array/concat.utils';
import { uniqueFilter } from '@/utils/toolkit/array/unique-filter.utils';
import { groupBy } from '@/utils/toolkit/object/group-by.utils';
import { isNotNullish, isNullish } from '@/utils/typing/is-nullish.predicate';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, type OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, defer, NEVER, type Observable, of } from 'rxjs';
import { catchError, debounceTime, filter, map, switchMap, tap } from 'rxjs/operators';
import type {
    BusinessIntelligenceDataPoint,
    BusinessIntelligenceDimensionCode,
    BusinessIntelligenceMeasureCode,
    BusinessIntelligenceMemberCode
} from '../../../interfaces/BusinessIntelligence.interface';
import { HighchartsStaticProvider } from '../../../providers/highcharts/highcharts.static.provider';
import { HighchartsChartComponent } from '../../highcharts-chart/highcharts-chart';
import { LoadingAndErrorComponent } from '../../loading-and-error/loading-and-error';
import { completeMeaningFullData } from '../business-intelligence-complete-data.helper';
import { BusinessIntelligenceRepresentationComponent } from '../business-intelligence-representation-component.class';
import {
    BusinessIntelligenceContinuumType,
    type BusinessIntelligenceRepresentationData
} from '../business-intelligence-representation-data.interface';
import {
    BusinessIntelligenceComboChartMeasureChartType,
    type BusinessIntelligenceRepresentationComponentConfig
} from './../../../interfaces/BusinessIntelligenceRepresentation.interface';

// This interface should be refactored when writing upgrade scripts for configs and implementing better data representation modularity
export interface BusinessIntelligenceLinesChartComponentConfig extends BusinessIntelligenceRepresentationComponentConfig {
    xAxisDimensionCode: BusinessIntelligenceDimensionCode;
    segmentationDimensionCode: BusinessIntelligenceDimensionCode;
    measures: {
        measureCode: BusinessIntelligenceMeasureCode;
        isCumulative: boolean;
        chartType: BusinessIntelligenceComboChartMeasureChartType;
        columnStackingGroup: number | null;
        color: ColorPaletteConfiguration;
    }[];
    displayZeros: boolean | null;
}

@UntilDestroy()
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./business-intelligence-lines-chart.scss'],
    selector: 'business-intelligence-lines-chart',
    templateUrl: 'business-intelligence-lines-chart.html',
    imports: [HighchartsChartComponent, LoadingAndErrorComponent, AsyncPipe]
})
export class BusinessIntelligenceLinesChartComponent
    extends BusinessIntelligenceRepresentationComponent<BusinessIntelligenceLinesChartComponentConfig>
    implements OnInit
{
    private _highchartsStaticProvider = inject(HighchartsStaticProvider);
    private _translateService = inject(TranslateService);

    chartConfig$!: Observable<Highcharts.Options>;

    noData: boolean = false;

    tooltip$ = new BehaviorSubject<{ tooltip: any; content$: Observable<string> } | null>(null);

    ngOnInit() {
        this.tooltip$
            .pipe(
                filter(isNotNullish),
                debounceTime(0),
                switchMap(({ tooltip, content$ }) => content$.pipe(map(content => ({ tooltip, content })))),
                untilDestroyed(this)
            )
            .subscribe(({ tooltip, content }) => {
                tooltip.label.attr({
                    text: content
                });
            });

        const tooltip$ = this.tooltip$;

        this.chartConfig$ = combineLatest([this._representationData$, this._config$]).pipe(
            // replace later with proper error handling for wrong configs
            filter(
                ([representationData, config]) =>
                    isNotNullish(representationData) && Object.keys(representationData.metadata).length > 0 && isNotNullish(config)
            ),
            tap(([representationData, config]) => {
                this.noData = false;

                const meaningfulData = this.getMeaningFullData(representationData, config);

                if (!meaningfulData.length) {
                    this.noData = true;
                }
            }),
            switchMap(([representationData, config]) => {
                return defer(() => {
                    const meaningfulData = this.getMeaningFullData(representationData, config);

                    const xAxisValues = meaningfulData.map(d => d.dimensions[config.xAxisDimensionCode]).filter(uniqueFilter);

                    const xAxisValuesIndexes = xAxisValues
                        .map((category, index) => ({ [category]: index }))
                        .reduce((acc, val) => ({ ...acc, ...val }), {});

                    const xAxisContinuity = representationData.metadata[config.xAxisDimensionCode].continuity;

                    const generateSeriesData = (
                        seriesData: BusinessIntelligenceDataPoint[],
                        measureCode: BusinessIntelligenceMeasureCode,
                        chartType: BusinessIntelligenceComboChartMeasureChartType,
                        accumulate: boolean
                    ): Observable<any> | any => {
                        if (isNullish(seriesData)) return [];
                        let accumulated = 0;
                        return combineLatestDeepObject(
                            seriesData.map(dp => {
                                return {
                                    x: xAxisContinuity
                                        ? xAxisContinuity.getContinuumValueFromValue$(
                                              xAxisContinuity.continuum === BusinessIntelligenceContinuumType.TIMESTAMP
                                                  ? new Date(dp.dimensions[config.xAxisDimensionCode])
                                                  : dp.dimensions[config.xAxisDimensionCode]
                                          )
                                        : xAxisValuesIndexes[dp.dimensions[config.xAxisDimensionCode]],
                                    y: this.shouldZeroBeDisplayed(accumulate, chartType, config.displayZeros)
                                        ? dp.measures[measureCode]
                                        : dp.measures[measureCode] === 0
                                          ? null
                                          : dp.measures[measureCode]
                                };
                            })
                        ).pipe(
                            map(series => {
                                if (xAxisContinuity) {
                                    return series.slice().sort((point1: any, point2: any) => point1.x - point2.x);
                                } else {
                                    return series
                                        .slice()
                                        .sort((point1: any, point2: any) => xAxisValuesIndexes[point1.x] - xAxisValuesIndexes[point2.x]);
                                }
                            }),
                            map(series => {
                                if (xAxisContinuity && accumulate) {
                                    return series.map((point: any) => {
                                        accumulated += point.y;
                                        return { x: point.x, y: accumulated };
                                    });
                                } else {
                                    return series;
                                }
                            })
                        );
                    };

                    const measureColorMap = buildDeterministicColorMap(new Set(config.measures.map(m => m.measureCode)));

                    const series = config.segmentationDimensionCode
                        ? config.measures
                              .map(measure => {
                                  if (this.shouldSegmentedMeasureBeSummed(measure, config.measures)) {
                                      return [
                                          {
                                              valueConfig: measure.measureCode,
                                              name: representationData.metadata[measure.measureCode]
                                                  .getName$()
                                                  .pipe(map(name => this.getCumulativeName(name, measure.isCumulative))),
                                              color: of(
                                                  measure.color
                                                      ? prettyFeelColorPalettes[measure.color?.paletteName][measure.color?.variant].solid
                                                      : (measureColorMap.get(measure.measureCode) ??
                                                            hashStringToColor(measure.measureCode, 70, 70))
                                              ),
                                              data: generateSeriesData(
                                                  Array.from(
                                                      groupBy(
                                                          meaningfulData,
                                                          dataPoint => dataPoint.dimensions[config.xAxisDimensionCode]
                                                      ).entries()
                                                  ).map(([sameX, pointsWithSameX]) => {
                                                      return {
                                                          dimensions: { [config.xAxisDimensionCode]: sameX },
                                                          measures: {
                                                              [measure.measureCode]: pointsWithSameX.reduce((acc, point) => {
                                                                  return acc + point.measures[measure.measureCode];
                                                              }, 0)
                                                          }
                                                      };
                                                  }),
                                                  measure.measureCode,
                                                  measure.chartType,
                                                  measure.isCumulative
                                              ),
                                              type: this.getSeriesType(measure.chartType),
                                              stack: measure.columnStackingGroup
                                          }
                                      ];
                                  } else {
                                      return Array.from(
                                          groupBy(
                                              meaningfulData,
                                              dataPoint => dataPoint.dimensions[config.segmentationDimensionCode]
                                          ).entries()
                                      ).map(([seriesCode, values]) => ({
                                          valueConfig: measure.measureCode,
                                          name: combineLatest([
                                              representationData.metadata[
                                                  config.segmentationDimensionCode
                                              ].valuesFormatting.formatAsStandaloneText$(seriesCode),
                                              super.getMemberName$(measure.measureCode, representationData)
                                          ]).pipe(
                                              map(([seriesName, valueName]) =>
                                                  this.getCumulativeName(`${seriesName} - ${valueName}`, measure.isCumulative)
                                              )
                                          ),
                                          color: representationData.metadata[
                                              config.segmentationDimensionCode
                                          ].valuesFormatting.formatAsColor$(seriesCode),
                                          data: generateSeriesData(values, measure.measureCode, measure.chartType, measure.isCumulative),
                                          type: this.getSeriesType(measure.chartType),
                                          stack: measure.columnStackingGroup
                                      }));
                                  }
                              })
                              .reduce(concatReducer, [])
                        : config.measures.map(measure => {
                              return {
                                  valueConfig: measure.measureCode,
                                  name: representationData.metadata[measure.measureCode]
                                      .getName$()
                                      .pipe(map(name => this.getCumulativeName(name, measure.isCumulative))),
                                  color: measure.color
                                      ? prettyFeelColorPalettes[measure.color?.paletteName][measure.color?.variant].solid
                                      : (measureColorMap.get(measure.measureCode) ?? hashStringToColor(measure.measureCode, 70, 70)),
                                  data: generateSeriesData(meaningfulData, measure.measureCode, measure.chartType, measure.isCumulative),
                                  type: this.getSeriesType(measure.chartType),
                                  stack: measure.columnStackingGroup
                              };
                          });

                    const getValueToFormatFromXValue$ = (xValue: number) =>
                        xAxisContinuity ? xAxisContinuity.getValueFromContinuumValue$(xValue) : of(xAxisValues[xValue]);

                    return combineLatestDeepObject(
                        this._highchartsStaticProvider.buildConfig(this._highchartsStaticProvider.COLUMN_MIN_SIZE_5, {
                            chart: {
                                zooming: {
                                    type: 'x'
                                }
                            },
                            // think about extending this to other charts
                            time: {
                                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
                            },
                            boost: {
                                // if necessary, we could work so that the chart works in boost mode.
                                // See that if performance issues arise
                                seriesThreshold: 10000
                            },
                            plotOptions: {
                                line: {
                                    marker: {
                                        enabled: false,
                                        symbol: 'circle'
                                    },
                                    // if necessary, we could work so that the chart works in boost mode.
                                    // See that if performance issues arise
                                    boostThreshold: 0, // disabled
                                    turboThreshold: 0 // disabled
                                },
                                column: {
                                    stacking: 'normal',
                                    shadow: false,
                                    borderWidth: 0,
                                    // if necessary, we could work so that the chart works in boost mode.
                                    // See that if performance issues arise
                                    boostThreshold: 0, // disabled
                                    turboThreshold: 0 // disabled
                                }
                            },
                            xAxis: {
                                labels: {
                                    formatter(this: Highcharts.AxisLabelsFormatterContextObject) {
                                        // TODO : handle async updates
                                        return extractValue(
                                            getValueToFormatFromXValue$(this.value as number).pipe(
                                                switchMap(valueToFormat =>
                                                    representationData.metadata[
                                                        config.xAxisDimensionCode
                                                    ].valuesFormatting.formatAsStandaloneText$(valueToFormat)
                                                )
                                            )
                                        );
                                    },
                                    padding: 12,
                                    autoRotationLimit: 120
                                },
                                type:
                                    xAxisContinuity && xAxisContinuity.continuum === BusinessIntelligenceContinuumType.TIMESTAMP
                                        ? 'datetime'
                                        : xAxisContinuity && xAxisContinuity.continuum === BusinessIntelligenceContinuumType.NUMBER
                                          ? 'linear'
                                          : 'category'
                            },
                            yAxis: config.measures.map(value => {
                                return {
                                    title: {
                                        text:
                                            config.measures.length > 1 ||
                                            (config.segmentationDimensionCode && config.segmentationDimensionCode.length)
                                                ? null
                                                : super
                                                      .getMemberName$(value.measureCode, representationData)
                                                      .pipe(map(name => this.getCumulativeName(name, value.isCumulative)))
                                    },
                                    labels: {
                                        formatter(this: Highcharts.AxisLabelsFormatterContextObject) {
                                            // TODO : handle async updates
                                            // TODO : use grouped text formatting when it will be available
                                            return extractValue(
                                                representationData.metadata[value.measureCode].valuesFormatting.formatAsStandaloneText$(
                                                    this.value
                                                )
                                            );
                                        }
                                    }
                                };
                            }),
                            tooltip: {
                                shared: true,
                                formatter(this: TooltipFormatterContext, tooltip: string) {
                                    const xAxisToDisplay$ = getValueToFormatFromXValue$(this.x as number).pipe(
                                        switchMap(valueToFormat =>
                                            representationData.metadata[config.xAxisDimensionCode].valuesFormatting.formatAsStandaloneText$(
                                                valueToFormat
                                            )
                                        )
                                    );

                                    const seriesToDisplay$ = combineLatestDeepObject(
                                        this.points?.map(point => {
                                            return {
                                                name: point.series.name,
                                                valueTodisplay: representationData.metadata[
                                                    (point.series.userOptions as any).valueConfig
                                                ].valuesFormatting.formatAsStandaloneText$(point.y),
                                                color: point.series.color,
                                                isHovered: (point.series as any).state === 'hover'
                                            };
                                        })
                                    );

                                    tooltip$.next({
                                        tooltip,
                                        content$: combineLatest([seriesToDisplay$, xAxisToDisplay$]).pipe(
                                            map(([seriesToDisplay, xAxisToDisplay]) => {
                                                const tooltipText = `<b> ${xAxisToDisplay} </b> <br/>`;

                                                return seriesToDisplay.reduce((text: string, serie: any) => {
                                                    let line = `<span style="color:${serie.color}">\u25CF</span> ${serie.name}: ${serie.valueTodisplay}`;

                                                    if (serie.isHovered && seriesToDisplay.length > 1) {
                                                        line = `<b>${line}</b>`;
                                                    }

                                                    return text + '<br/>' + line;
                                                }, tooltipText);
                                            })
                                        )
                                    });
                                    return;
                                }
                            },
                            legend: {
                                enabled:
                                    config.measures.length > 1 ||
                                    (config.segmentationDimensionCode && config.segmentationDimensionCode.length)
                                        ? true
                                        : false
                            },
                            series
                        })
                    );
                }).pipe(
                    catchError(err => {
                        console.error('Error while updating lines chart config ::', err);
                        return NEVER;
                    })
                );
            })
        );
    }

    getExportMembers(config: BusinessIntelligenceLinesChartComponentConfig): BusinessIntelligenceMemberCode[] {
        return [config.xAxisDimensionCode, config.segmentationDimensionCode, ...config.measures.map(value => value.measureCode)];
    }

    private shouldZeroBeDisplayed(
        accumulate: boolean,
        chartType: BusinessIntelligenceComboChartMeasureChartType,
        forceDisplayZeroes: boolean | null
    ): boolean {
        if (isNullish(forceDisplayZeroes)) {
            return accumulate || chartType === BusinessIntelligenceComboChartMeasureChartType.LINE;
        }
        return forceDisplayZeroes;
    }

    private getCumulativeName(name: string, isCumulative: boolean): string {
        if (isCumulative) {
            return `${name} (${this._translateService.instant('TAILORED_WIDGET.BUSINESS_INTELLIGENCE.COMBO_CHART.CUMULATED')})`;
        } else {
            return name;
        }
    }

    private getMeaningFullData(
        representationData: BusinessIntelligenceRepresentationData,
        config: BusinessIntelligenceLinesChartComponentConfig
    ): BusinessIntelligenceDataPoint[] {
        const xAxisContinuity = representationData.metadata[config.xAxisDimensionCode].continuity;

        const completedData = completeMeaningFullData(
            representationData.data,
            config.xAxisDimensionCode,
            xAxisContinuity,
            config.measures.map(measure => ({
                measureCode: measure.measureCode,
                isExtensive: representationData.metadata[measure.measureCode].isExtensive
            })),
            config.segmentationDimensionCode
        );

        const measureCodes = config.measures.map(value => value.measureCode);

        function isDataPointMeaningfulInChart(
            dataPoint: BusinessIntelligenceDataPoint,
            measureCodes: BusinessIntelligenceMeasureCode[]
        ): boolean {
            return measureCodes.some(
                measureCode => isNotNullish(dataPoint.measures[measureCode]) || !representationData.metadata[measureCode].isExtensive
            );
        }

        const filteredData = completedData.filter(d => {
            return xAxisContinuity
                ? isDataPointMeaningfulInChart(d, measureCodes) && isNotNullish(d.dimensions[config.xAxisDimensionCode])
                : isDataPointMeaningfulInChart(d, measureCodes);
        });

        return filteredData;
    }

    private getSeriesType(chartType: BusinessIntelligenceComboChartMeasureChartType): string {
        switch (chartType) {
            case BusinessIntelligenceComboChartMeasureChartType.COLUMN:
                return 'column';
            case BusinessIntelligenceComboChartMeasureChartType.LINE:
                return 'line';
        }
    }

    private shouldSegmentedMeasureBeSummed(
        measure: BusinessIntelligenceLinesChartComponentConfig['measures'][number],
        measures: BusinessIntelligenceLinesChartComponentConfig['measures']
    ) {
        return (
            measure.chartType === BusinessIntelligenceComboChartMeasureChartType.LINE &&
            measures.some(measure => measure.chartType !== BusinessIntelligenceComboChartMeasureChartType.LINE)
        );
    }
}
